import {
  ASK_ZGHARTA_SELECTION_TYPES,
  boundAskZghartaMessages,
} from './askZghartaPlannerService';

export const ASK_ZGHARTA_CONVERSATION_ACTIONS = {
  userTurnAdded: 'user_turn_added',
  planReceived: 'plan_received',
  reset: 'reset',
};

export const createAskZghartaConversationState = () => ({
  turns: [],
  currentPlanIndex: null,
});

// Catalog refs the plan actually rendered, in section order, deduped by
// type:id. These become the assistant turn's ids-only selections — the wire
// bound (boundAskZghartaMessages) owns the per-turn cap, not state.
// Fallback/local plans (planAskZghartaFromCatalog) carry raw Supabase ids,
// which can be numeric; the plan validator accepts them by stringifying, so
// coerce here too — the wire shape (and the server) requires string ids.
const refIdString = id => {
  if (typeof id === 'string') return id;
  if (typeof id === 'number' && Number.isFinite(id)) return String(id);
  return '';
};

const extractPlanSelections = plan => {
  const seen = new Set();
  const selections = [];
  (plan.sections || []).forEach(section => {
    (section?.items || []).forEach(item => {
      const ref = item?.source === 'catalog' ? item.ref : null;
      if (!ref || !ASK_ZGHARTA_SELECTION_TYPES.has(ref.type)) return;
      const id = refIdString(ref.id);
      if (id.length === 0) return;
      const key = `${ref.type}:${id}`;
      if (seen.has(key)) return;
      seen.add(key);
      selections.push({ type: ref.type, id });
    });
  });
  return selections;
};

// Pure conversation state for the ask-Zgharta UI (#211 wires it up). The
// endpoint stays stateless: this state lives client-side only, and only its
// bounded wire projection (buildMessagesForRequest) ever leaves the browser.
export const askZghartaConversationReducer = (
  state = createAskZghartaConversationState(),
  action,
) => {
  if (!action || typeof action !== 'object') return state;

  if (action.type === ASK_ZGHARTA_CONVERSATION_ACTIONS.userTurnAdded) {
    if (typeof action.content !== 'string' || !action.content.trim()) return state;
    return {
      ...state,
      turns: [...state.turns, { role: 'user', content: action.content }],
    };
  }

  if (action.type === ASK_ZGHARTA_CONVERSATION_ACTIONS.planReceived) {
    if (!action.plan || typeof action.plan !== 'object') return state;
    const turn = {
      role: 'assistant',
      plan: action.plan,
      selections: extractPlanSelections(action.plan),
    };
    return {
      turns: [...state.turns, turn],
      currentPlanIndex: state.turns.length,
    };
  }

  if (action.type === ASK_ZGHARTA_CONVERSATION_ACTIONS.reset) {
    return createAskZghartaConversationState();
  }

  return state;
};

// Bounded wire-format projection of the conversation: plan objects are
// stripped (the wire shape keeps ids-only selections), turns are capped at
// the newest 8 with user content trimmed, and the user-first head guard is
// applied — all delegated to the shared serializer bound.
export const buildMessagesForRequest = state => boundAskZghartaMessages(state?.turns);

export const getCurrentAskZghartaPlan = state => {
  if (!state || state.currentPlanIndex == null) return null;
  return state.turns[state.currentPlanIndex]?.plan || null;
};
